import React from 'react';
import {
  Text,
  Button,
  Spinner,
  Input,
  Field,
  Dropdown,
  Option,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { SearchRegular, DismissCircleRegular } from '@fluentui/react-icons';
import { useTopBarTitleNode, useTopBarActions } from '../../contexts/TopBarContext';

const useStyles = makeStyles({
  page: {
    maxWidth: '1800px',
    margin: '0 auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  stickyBar: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '8px 40px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    justifyContent: 'flex-start',
    minWidth: 0,
  },
  scrollArea: {
    flex: '1 1 auto',
    overflowY: 'auto',
    padding: '16px 40px',
  },
  searchField: {
    flex: '1 1 auto',
    maxWidth: '600px',
    minWidth: '240px',
  },
  dropdown: {
    minWidth: '150px',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  cardGrid: {
    display: 'grid',
    gap: '16px',
  },
  loadingCenter: {
    display: 'flex',
    justifyContent: 'center',
    padding: '64px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 24px',
    gap: '16px',
    textAlign: 'center',
  },
  emptyIcon: {
    display: 'flex',
    color: tokens.colorNeutralForeground4,
    '& > svg': { width: '48px', height: '48px' },
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  emptyDescription: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    maxWidth: '400px',
    textAlign: 'center',
  },
});

export interface SortOption {
  value: string;
  label: string;
}

interface CardListPageProps {
  /** Page title shown in TopBar */
  title: string;
  /** Inline stats shown next to title in TopBar (e.g. "4 dashboards · 3 AI generated · 53 charts") */
  subtitle?: string;
  /** Header action button (rendered in TopBar) */
  headerAction?: React.ReactNode;
  /** Search */
  searchPlaceholder?: string;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  /** Sort */
  sortOptions: SortOption[];
  sortValue: string;
  onSortChange: (v: string) => void;
  /** State */
  isLoading: boolean;
  /** True when there are no items at all (before filtering) */
  isEmpty: boolean;
  /** Total filtered count (for no-results message) */
  filteredCount: number;
  /** Always-visible card rendered before filtered items (e.g. "Create" placeholder) */
  persistentCard?: React.ReactNode;
  /** Empty state content */
  emptyIcon?: React.ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  /** Actionable load or mutation error displayed above page content. */
  errorMessage?: string | null;
  /** Layout: 'list' = single column rows, 'grid' = responsive multi-column */
  layout?: 'list' | 'grid';
  /** Minimum column width for grid layout (default: 280px) */
  gridMinWidth?: string;
  /** Card grid children */
  children: React.ReactNode;
}

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}

const EmptyState: React.FC<EmptyStateProps & { styles: ReturnType<typeof useStyles> }> = ({
  icon,
  title,
  description,
  action,
  styles,
}) => (
  <div className={styles.emptyState}>
    {icon && <span className={styles.emptyIcon}>{icon}</span>}
    <Text className={styles.emptyTitle}>{title}</Text>
    {description && <Text className={styles.emptyDescription}>{description}</Text>}
    {action}
  </div>
);

const CardListPage: React.FC<CardListPageProps> = ({
  title,
  subtitle,
  headerAction,
  persistentCard,
  searchPlaceholder = 'Search...',
  searchQuery,
  onSearchChange,
  sortOptions,
  sortValue,
  onSortChange,
  isLoading,
  isEmpty,
  filteredCount,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  emptyAction,
  errorMessage,
  layout = 'list',
  gridMinWidth = '280px',
  children,
}) => {
  const styles = useStyles();

  const titleNode = React.useMemo(() => (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
      <span style={{ fontWeight: 600, fontSize: '20px' }}>{title}</span>
      {subtitle && (
        <span style={{ fontSize: '13px', fontWeight: 400, color: tokens.colorNeutralForeground3 }}>
          {subtitle}
        </span>
      )}
    </span>
  ), [title, subtitle]);

  useTopBarTitleNode(titleNode);
  useTopBarActions(headerAction ?? null);

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingCenter}>
          <Spinner size="large" label={`Loading ${title.toLowerCase()}...`} />
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className={styles.page}>
        {errorMessage && <MessageBar intent="error"><MessageBarBody>{errorMessage}</MessageBarBody></MessageBar>}
        <EmptyState
          styles={styles}
          icon={emptyIcon}
          title={emptyTitle ?? `No ${title.toLowerCase()}`}
          description={emptyDescription}
          action={emptyAction}
        />
      </div>
    );
  }

  const currentSortLabel = sortOptions.find((o) => o.value === sortValue)?.label ?? sortOptions[0]?.label ?? '';

  return (
    <div className={styles.page}>
      {errorMessage && <MessageBar intent="error"><MessageBarBody>{errorMessage}</MessageBarBody></MessageBar>}
      {/* Sticky toolbar: Search + Sort */}
      <div className={styles.stickyBar}>
        <div className={styles.toolbar}>
          <div className={styles.searchField}>
            <Field>
              <Input
                size="medium"
                contentBefore={<SearchRegular />}
                contentAfter={
                  searchQuery ? (
                    <Button
                      appearance="subtle"
                      icon={<DismissCircleRegular />}
                      size="small"
                      onClick={() => onSearchChange('')}
                      aria-label="Clear search"
                    />
                  ) : undefined
                }
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(_, data) => onSearchChange(data.value)}
              />
            </Field>
          </div>
          <Dropdown
            size="medium"
            className={styles.dropdown}
            placeholder="Sort"
            value={currentSortLabel}
            selectedOptions={[sortValue]}
            onOptionSelect={(_, data) => onSortChange(data.optionValue ?? sortOptions[0]?.value ?? '')}
          >
            {sortOptions.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Dropdown>
        </div>
      </div>

      {/* Scrollable cards area */}
      <div className={styles.scrollArea}>
        <div
          className={layout === 'grid' ? styles.cardGrid : styles.cardList}
          style={layout === 'grid' ? { gridTemplateColumns: `repeat(auto-fill, minmax(${gridMinWidth}, 1fr))` } : undefined}
        >
          {persistentCard}
          {filteredCount > 0 ? children : null}
        </div>
        {filteredCount === 0 && (
          <EmptyState
            styles={styles}
            icon={<SearchRegular />}
            title="No matching results"
            description="Try a different search term."
          />
        )}
      </div>
    </div>
  );
};

export default CardListPage;